import { computed, ref } from 'vue'
import type {
  CreateMenuRequest,
  CreateClientRoleRequest,
  CreateClientUserRequest,
  CreateDictItemRequest,
  CreateDictTypeRequest,
  CreateDeptRequest,
  CreateModuleUserRequest,
  CreateParamRequest,
  CreateRoleRequest,
  CreateUserRequest,
  CurrentPermissionView,
  ClientRbacCatalog,
  ClientRole,
  ClientUser,
  DictItem,
  DictType,
  Dept,
  ModuleMetadata,
  ModuleUser,
  OpenApp,
  Role,
  SystemParam,
  TenantMenu,
  TenantOpenAppGrant,
  UpdateDictItemRequest,
  UpdateDictTypeRequest,
  UpdateDeptRequest,
  UpdateClientRoleRequest,
  UpdateClientUserRequest,
  UpdateModuleUserRequest,
  UpdateModuleUserStatusRequest,
  UpdateMenuRequest,
  UpdateParamRequest,
  UpdateRoleRequest,
  UpdateTenantOpenAppGrantRequest,
  User
} from '@xjlv2/api-client'
import { SYSTEM_PERMISSIONS, CRASH_ISSUES_VIEW_PERMISSION } from '@xjlv2/api-client'
import { useSessionStore } from '@xjlv2/auth'
import type {
  ApiPermissionRow,
  DataScopeRow,
  ModuleMenuView,
  ModuleMenuItem,
  MenuReorderItem,
  PermissionGroup,
  PermissionRow,
  TenantMenuGroup,
  UserMenuView,
  WorkspaceState,
  WorkspaceTabDefinition
} from '../types'
import {
  isAuthorizedRemoteMenu,
  isLegacyMaterialMenu,
  isLegacyProductMenu,
  mergeMenuDefinitions,
  resolveShellMenuKind
} from '../utils/menuArchitecture'

const emptyCatalog = (): CurrentPermissionView => ({
  permissions: [],
  apis: [],
  dataResources: []
})

const emptyClientRbacCatalog = (): ClientRbacCatalog => ({
  menus: [],
  permissions: []
})

const allTabs: WorkspaceTabDefinition[] = [
  { key: 'dashboard', label: '工作台', permission: SYSTEM_PERMISSIONS.workspaceAccess },
  { key: 'notifications', label: '我的通知', permission: SYSTEM_PERMISSIONS.notificationRead },
  { key: 'behaviorAnalytics', label: '行为分析', permission: SYSTEM_PERMISSIONS.workspaceAnalyticsView },
  // crash-center 模块自有权限码（后端 CrashPermissions.CRASH_ISSUES_VIEW，V367 种子授予 ACTIVE 租户）。
  { key: 'crashIssues', label: '错误监控', permission: CRASH_ISSUES_VIEW_PERMISSION },
  {
    key: 'permissionManagement',
    label: '权限管理',
    permissions: [
      SYSTEM_PERMISSIONS.permissionView,
      SYSTEM_PERMISSIONS.menuRead,
      SYSTEM_PERMISSIONS.deptRead,
      SYSTEM_PERMISSIONS.rbacRead,
      SYSTEM_PERMISSIONS.clientRbacRead,
      SYSTEM_PERMISSIONS.paramRead,
      SYSTEM_PERMISSIONS.dictRead,
      SYSTEM_PERMISSIONS.openAppRead
    ]
  }
]

function compareTenantMenus(left: Pick<TenantMenu, 'sortOrder' | 'menuCode'>, right: Pick<TenantMenu, 'sortOrder' | 'menuCode'>) {
  return left.sortOrder - right.sortOrder || left.menuCode.localeCompare(right.menuCode)
}

interface FlattenedTenantMenu {
  menu: TenantMenu
  depth: number
}

function flattenTenantMenuTree(menus: TenantMenu[]) {
  const sortedMenus = [...menus].sort(compareTenantMenus)
  const menuCodes = new Set(sortedMenus.map((menu) => menu.menuCode))
  const childrenByParent = new Map<string, TenantMenu[]>()
  for (const menu of sortedMenus) {
    const parentCode = menu.parentCode && menuCodes.has(menu.parentCode) ? menu.parentCode : ''
    const children = childrenByParent.get(parentCode) ?? []
    children.push(menu)
    childrenByParent.set(parentCode, children)
  }

  const rows: FlattenedTenantMenu[] = []
  const appendMenu = (menu: TenantMenu, depth: number) => {
    rows.push({ menu, depth })
    for (const child of childrenByParent.get(menu.menuCode) ?? []) {
      appendMenu(child, depth + 1)
    }
  }
  for (const root of childrenByParent.get('') ?? []) {
    appendMenu(root, 0)
  }
  return rows
}

function toModuleMenuItem(menu: TenantMenu, module: ModuleMetadata, depth = 0): ModuleMenuItem {
  const kind = resolveShellMenuKind(module.moduleCode, menu.menuCode)
  return {
    code: menu.menuCode,
    parentCode: menu.parentCode,
    name: menu.name,
    icon: menu.icon,
    menuType: menu.menuType,
    openMode: menu.openMode,
    path: menu.path,
    permission: menu.permission,
    remoteEntry: menu.remoteEntry,
    exposedModule: menu.exposedModule,
    enabled: menu.enabled,
    hidden: menu.hidden,
    moduleCode: module.moduleCode,
    moduleName: module.moduleName,
    depth,
    hasChildren: false,
    kind
  }
}

function markChildState(menus: ModuleMenuItem[]) {
  const parentCodes = new Set(menus.map((menu) => menu.parentCode).filter((code): code is string => Boolean(code)))
  return menus.map((menu) => ({
    ...menu,
    hasChildren: parentCodes.has(menu.code)
  }))
}

function normalizeMenuHierarchy(menus: ModuleMenuItem[]) {
  const byCode = new Map(menus.map((menu) => [menu.code, menu]))
  const depthOf = (menu: ModuleMenuItem, visited = new Set<string>()): number => {
    const parentCode = menu.parentCode
    if (!parentCode || visited.has(parentCode)) return 0
    const parent = byCode.get(parentCode)
    if (!parent) return 0
    const nextVisited = new Set(visited)
    nextVisited.add(menu.code)
    return 1 + depthOf(parent, nextVisited)
  }
  return markChildState(menus.map((menu) => ({ ...menu, depth: depthOf(menu) })))
}

// 后端对部分账号（如租户管理员）会返回重复的菜单行（同一 menuCode 多份拷贝），
// 按 menuCode 去重，避免侧边栏 / 角色授权树 / 菜单可见性面板重复渲染
function dedupeTenantMenus(menus: TenantMenu[]): TenantMenu[] {
  const seen = new Set<string>()
  const result: TenantMenu[] = []
  for (const menu of menus) {
    if (seen.has(menu.menuCode)) continue
    seen.add(menu.menuCode)
    result.push(menu)
  }
  return result
}

export function useSaasWorkspace() {
  const session = useSessionStore()
  const loading = ref(false)
  const error = ref('')
  const state = ref<WorkspaceState>({
    roles: [],
    clientRoles: [],
    clientRbacCatalog: emptyClientRbacCatalog(),
    depts: [],
    params: [],
    dictTypes: [],
    systemModules: [],
    menus: [],
    openApps: [],
    openAppGrants: [],
    permissionCatalog: emptyCatalog()
  })

  const canViewPermissions = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.permissionView))
  const canReadMenus = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.menuRead))
  const canCreateMenus = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.menuCreate))
  const canUpdateMenus = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.menuUpdate))
  const canDeleteMenus = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.menuDelete))
  const canReadRbac = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.rbacRead))
  const canCreateRole = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.roleCreate))
  const canUpdateRole = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.roleUpdate))
  const canCreateUser = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.userCreate))
  const canReadClientRbac = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.clientRbacRead))
  const canCreateClientRole = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.clientRoleCreate))
  const canUpdateClientRole = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.clientRoleUpdate))
  const canCreateClientUser = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.clientUserCreate))
  const canUpdateClientUser = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.clientUserUpdate))
  const canReadDepts = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.deptRead))
  const canCreateDept = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.deptCreate))
  const canUpdateDept = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.deptUpdate))
  const canReadParams = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.paramRead))
  const canCreateParam = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.paramCreate))
  const canUpdateParam = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.paramUpdate))
  const canDeleteParam = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.paramDelete))
  const canReadDicts = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.dictRead))
  const canCreateDict = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.dictCreate))
  const canUpdateDict = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.dictUpdate))
  const canDeleteDict = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.dictDelete))
  const canReadDictItems = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.dictItemRead))
  const canCreateDictItem = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.dictItemCreate))
  const canUpdateDictItem = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.dictItemUpdate))
  const canDeleteDictItem = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.dictItemDelete))
  const canReadModuleUsers = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.moduleUserRead))
  const canCreateModuleUser = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.moduleUserCreate))
  const canUpdateModuleUser = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.moduleUserUpdate))
  const canReadOpenApps = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.openAppRead))
  const canGrantOpenApps = computed(() => session.hasPermission(SYSTEM_PERMISSIONS.openAppGrant))

  const availableTabs = computed(() =>
    allTabs.filter((tab) => {
      if (tab.permission && session.hasPermission(tab.permission)) return true
      if (tab.permissions?.some((permission) => session.hasPermission(permission))) return true
      return !tab.permission && !tab.permissions
    })
  )

  const permittedModules = computed<ModuleMetadata[]>(() =>
    session.modules.filter((module) => session.user?.modules.includes(module.moduleCode))
  )

  const menuViews = computed<ModuleMenuView[]>(() =>
    permittedModules.value.map((module) => {
      const configuredMenus = flattenTenantMenuTree(
        state.value.menus.filter((menu) => menu.moduleCode === module.moduleCode)
      )
      const configuredItems = configuredMenus.map(({ menu, depth }) => toModuleMenuItem(menu, module, depth))
      const catalogItems = module.menus.map((menu) => ({
        ...menu,
        moduleCode: module.moduleCode,
        moduleName: module.moduleName,
        depth: 0,
        hasChildren: false,
        kind: resolveShellMenuKind(module.moduleCode, menu.code)
      }))
      const sourceMenus = normalizeMenuHierarchy(mergeMenuDefinitions(configuredItems, catalogItems))
      const currentProductMenus = sourceMenus.filter(
        (menu) => !isLegacyMaterialMenu(module.moduleCode, menu.code) && !isLegacyProductMenu(menu.code)
      )
      const visibleMenus = currentProductMenus.filter(
        (menu) =>
          menu.kind !== 'module-user' &&
          menu.enabled !== false &&
          !menu.hidden &&
          menu.menuType !== 'BUTTON' &&
          isAuthorizedRemoteMenu({
            tenantId: session.user?.tenantId ?? null,
            moduleCode: module.moduleCode,
            menuCode: menu.code,
            permission: menu.permission,
            entitledModules: session.user?.modules ?? [],
            effectiveMenuCodes: session.user?.menuCodes,
            grantedPermissions: session.permissions
          })
      )

      return {
        moduleCode: module.moduleCode,
        moduleName: module.moduleName,
        totalMenus: currentProductMenus.length,
        visibleMenus
      }
    }).filter((module) => module.visibleMenus.length > 0)
  )

  const menuGroups = computed<TenantMenuGroup[]>(() => {
    const systemMenus = state.value.menus
      .filter((menu) => menu.moduleCode === 'system')
      .sort(compareTenantMenus)
    const systemGroups: TenantMenuGroup[] = systemMenus.length
      ? [
          {
            moduleCode: 'system',
            moduleName: '权限管理',
            menus: systemMenus
          }
        ]
      : []
    const moduleGroups = permittedModules.value.map((module) => ({
      moduleCode: module.moduleCode,
      moduleName: module.moduleName,
      menus: state.value.menus
        .filter((menu) => menu.moduleCode === module.moduleCode)
        .sort(compareTenantMenus)
    }))
    return [...systemGroups, ...moduleGroups]
  })

  const effectivePermissionRows = computed<PermissionRow[]>(() =>
    state.value.permissionCatalog.permissions.map((permission) => ({
      code: permission,
      granted: session.hasPermission(permission),
      source: sourceForPermission(permission)
    }))
  )

  const permissionGroups = computed<PermissionGroup[]>(() => {
    const groups = new Map<string, PermissionRow[]>()
    for (const permission of effectivePermissionRows.value) {
      const rows = groups.get(permission.source) ?? []
      rows.push(permission)
      groups.set(permission.source, rows)
    }
    return Array.from(groups.entries()).map(([source, permissions]) => ({
      source,
      permissions
    }))
  })

  const apiPermissionRows = computed<ApiPermissionRow[]>(() =>
    state.value.permissionCatalog.apis.map((api) => ({
      ...api,
      granted: session.hasPermission(api.permission)
    }))
  )

  const dataScopeRows = computed<DataScopeRow[]>(() =>
    state.value.permissionCatalog.dataResources.map((resource) => ({
      ...resource,
      grants: state.value.roles.flatMap((role) =>
        role.dataScopes.filter((scope) => scope.resourceCode === resource.code)
      )
    }))
  )

  async function refreshWorkspace() {
    if (!session.user) return

    loading.value = true
    error.value = ''
    try {
      const api = session.createApiClient()
      const [
        roles,
        clientRoles,
        clientRbacCatalog,
        depts,
        params,
        dictTypes,
        systemModules,
        openApps,
        openAppGrants,
        permissionCatalog
      ] = await Promise.all([
        canReadRbac.value
          ? api.get<Role[]>('/api/saas/system/rbac/roles')
          : Promise.resolve([]),
        canReadClientRbac.value
          ? api.get<ClientRole[]>('/api/saas/system/client-rbac/roles')
          : Promise.resolve([]),
        canReadClientRbac.value
          ? api.get<ClientRbacCatalog>('/api/saas/system/client-rbac/catalog')
          : Promise.resolve(emptyClientRbacCatalog()),
        canReadDepts.value
          ? api.get<Dept[]>('/api/saas/system/rbac/depts')
          : Promise.resolve([]),
        canReadParams.value
          ? api.get<SystemParam[]>('/api/saas/system/params')
          : Promise.resolve([]),
        canReadDicts.value
          ? api.get<DictType[]>('/api/saas/system/dicts')
          : Promise.resolve([]),
        Promise.resolve([]),
        canReadOpenApps.value
          ? api.get<OpenApp[]>('/api/saas/system/open-platform/apps')
          : Promise.resolve([]),
        canReadOpenApps.value
          ? api.get<TenantOpenAppGrant[]>('/api/saas/system/open-platform/grants')
          : Promise.resolve([]),
        canViewPermissions.value
          ? api.get<CurrentPermissionView>('/api/saas/current/permissions')
          : Promise.resolve(emptyCatalog())
      ])
      state.value = {
        roles,
        clientRoles,
        clientRbacCatalog,
        depts,
        params,
        dictTypes,
        systemModules,
        menus: state.value.menus,
        openApps,
        openAppGrants,
        permissionCatalog
      }
      if (session.signedIn) {
        try {
          await session.refreshSession()
        } catch (_e) {
          // session 刷新失败不影响工作台本身继续渲染
        }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : '工作台加载失败'
    } finally {
      loading.value = false
    }
  }

  async function loadMenus() {
    if (!session.user || !canReadMenus.value) return

    const menus = await session.createApiClient()
      .get<TenantMenu[]>('/api/saas/system/menus')
      .then(dedupeTenantMenus)
    state.value = { ...state.value, menus }
  }


  async function createRole(payload: CreateRoleRequest) {
    const api = session.createApiClient()
    await api.post<Role>('/api/saas/system/rbac/roles', payload)
    await refreshWorkspace()
  }

  async function updateRole(roleId: number, payload: UpdateRoleRequest) {
    const api = session.createApiClient()
    await api.put<Role>(`/api/saas/system/rbac/roles/${roleId}`, payload)
    await refreshWorkspace()
  }

  async function createUser(payload: CreateUserRequest) {
    const api = session.createApiClient()
    await api.post<User>('/api/saas/system/rbac/users', payload)
    await refreshWorkspace()
  }

  async function createClientRole(payload: CreateClientRoleRequest) {
    const api = session.createApiClient()
    await api.post<ClientRole>('/api/saas/system/client-rbac/roles', payload)
    await refreshWorkspace()
  }

  async function updateClientRole(roleId: number, payload: UpdateClientRoleRequest) {
    const api = session.createApiClient()
    await api.put<ClientRole>(`/api/saas/system/client-rbac/roles/${roleId}`, payload)
    await refreshWorkspace()
  }

  async function createClientUser(payload: CreateClientUserRequest) {
    const api = session.createApiClient()
    await api.post<ClientUser>('/api/saas/system/client-rbac/users', payload)
    await refreshWorkspace()
  }

  async function updateClientUser(userId: number, payload: UpdateClientUserRequest) {
    const api = session.createApiClient()
    await api.put<ClientUser>(`/api/saas/system/client-rbac/users/${userId}`, payload)
    await refreshWorkspace()
  }

  async function createModuleUser(payload: CreateModuleUserRequest) {
    const api = session.createApiClient()
    await api.post<ModuleUser>('/api/saas/system/module-users', payload)
    await refreshWorkspace()
  }

  async function updateModuleUser(userId: number, payload: UpdateModuleUserRequest) {
    const api = session.createApiClient()
    await api.put<ModuleUser>(`/api/saas/system/module-users/${userId}`, payload)
    await refreshWorkspace()
  }

  async function updateModuleUserStatus(userId: number, payload: UpdateModuleUserStatusRequest) {
    const api = session.createApiClient()
    await api.put<ModuleUser>(`/api/saas/system/module-users/${userId}/status`, payload)
    await refreshWorkspace()
  }

  async function createDept(payload: CreateDeptRequest) {
    const api = session.createApiClient()
    await api.post<Dept>('/api/saas/system/rbac/depts', payload)
    await refreshWorkspace()
  }

  async function updateDept(deptId: number, payload: UpdateDeptRequest) {
    const api = session.createApiClient()
    await api.put<Dept>(`/api/saas/system/rbac/depts/${deptId}`, payload)
    await refreshWorkspace()
  }

  async function createParam(payload: CreateParamRequest) {
    const api = session.createApiClient()
    await api.post<SystemParam>('/api/saas/system/params', payload)
    await refreshWorkspace()
  }

  async function updateParam(code: string, payload: UpdateParamRequest) {
    const api = session.createApiClient()
    await api.put<SystemParam>(`/api/saas/system/params/${encodeURIComponent(code)}`, payload)
    await refreshWorkspace()
  }

  async function deleteParam(code: string) {
    const api = session.createApiClient()
    await api.delete<void>(`/api/saas/system/params/${encodeURIComponent(code)}`)
    await refreshWorkspace()
  }

  async function createDict(payload: CreateDictTypeRequest) {
    const api = session.createApiClient()
    await api.post<DictType>('/api/saas/system/dicts', payload)
    await refreshWorkspace()
  }

  async function updateDict(type: string, payload: UpdateDictTypeRequest) {
    const api = session.createApiClient()
    await api.put<DictType>(`/api/saas/system/dicts/${encodeURIComponent(type)}`, payload)
    await refreshWorkspace()
  }

  async function deleteDict(type: string) {
    const api = session.createApiClient()
    await api.delete<void>(`/api/saas/system/dicts/${encodeURIComponent(type)}`)
    await refreshWorkspace()
  }

  async function createDictItem(type: string, payload: CreateDictItemRequest) {
    const api = session.createApiClient()
    await api.post<DictItem>(`/api/saas/system/dicts/${encodeURIComponent(type)}/items`, payload)
    await refreshWorkspace()
  }

  async function updateDictItem(type: string, value: string, payload: UpdateDictItemRequest) {
    const api = session.createApiClient()
    await api.put<DictItem>(
      `/api/saas/system/dicts/${encodeURIComponent(type)}/items/${encodeURIComponent(value)}`,
      payload
    )
    await refreshWorkspace()
  }

  async function reorderDictItems(type: string, values: string[]) {
    const api = session.createApiClient()
    await api.put<DictItem[]>(`/api/saas/system/dicts/${encodeURIComponent(type)}/items/sort`, { values })
    await refreshWorkspace()
  }

  async function deleteDictItem(type: string, value: string) {
    const api = session.createApiClient()
    await api.delete<void>(`/api/saas/system/dicts/${encodeURIComponent(type)}/items/${encodeURIComponent(value)}`)
    await refreshWorkspace()
  }

  async function updateMenu(menuCode: string, payload: UpdateMenuRequest) {
    const api = session.createApiClient()
    await api.put<TenantMenu>(`/api/saas/system/menus/${encodeURIComponent(menuCode)}`, payload)
    await loadMenus()
  }

  async function reorderMenus(updates: MenuReorderItem[]) {
    if (updates.length === 0) return
    const api = session.createApiClient()
    for (const update of updates) {
      await api.put<TenantMenu>(`/api/saas/system/menus/${encodeURIComponent(update.menuCode)}`, update.payload)
    }
    await loadMenus()
  }

  async function createMenu(payload: CreateMenuRequest) {
    const api = session.createApiClient()
    await api.post<TenantMenu>('/api/saas/system/menus', payload)
    await loadMenus()
  }

  async function deleteMenu(menuCode: string) {
    const api = session.createApiClient()
    await api.delete<void>(`/api/saas/system/menus/${encodeURIComponent(menuCode)}`)
    await loadMenus()
  }

  async function updateOpenAppGrant(appId: number, payload: UpdateTenantOpenAppGrantRequest) {
    const api = session.createApiClient()
    await api.put<TenantOpenAppGrant>(`/api/saas/system/open-platform/grants/${appId}`, payload)
    await refreshWorkspace()
  }

  function sourceForPermission(permission: string) {
    if (permission.startsWith('system:')) return 'SaaS System'
    if (permission.startsWith('decoration:')) return '装修'
    if (permission.startsWith('crm:')) return 'CRM'
    if (permission.startsWith('erp:')) return 'ERP'
    return 'Module'
  }

  return {
    session,
    state,
    loading,
    error,
    canViewPermissions,
    canReadMenus,
    canCreateMenus,
    canUpdateMenus,
    canDeleteMenus,
    canReadRbac,
    canCreateRole,
    canUpdateRole,
    canCreateUser,
    canReadClientRbac,
    canCreateClientRole,
    canUpdateClientRole,
    canCreateClientUser,
    canUpdateClientUser,
    canReadDepts,
    canCreateDept,
    canUpdateDept,
    canReadParams,
    canCreateParam,
    canUpdateParam,
    canDeleteParam,
    canReadDicts,
    canCreateDict,
    canUpdateDict,
    canDeleteDict,
    canReadDictItems,
    canCreateDictItem,
    canUpdateDictItem,
    canDeleteDictItem,
    canReadModuleUsers,
    canCreateModuleUser,
    canUpdateModuleUser,
    canReadOpenApps,
    canGrantOpenApps,
    availableTabs,
    permittedModules,
    menuViews,
    menuGroups,
    effectivePermissionRows,
    permissionGroups,
    apiPermissionRows,
    dataScopeRows,
    refreshWorkspace,
    loadMenus,
    createRole,
    updateRole,
    createUser,
    createClientRole,
    updateClientRole,
    createClientUser,
    updateClientUser,
    createModuleUser,
    updateModuleUser,
    updateModuleUserStatus,
    createDept,
    updateDept,
    createParam,
    updateParam,
    deleteParam,
    createDict,
    updateDict,
    deleteDict,
    createDictItem,
    updateDictItem,
    deleteDictItem,
    reorderDictItems,
    createMenu,
    updateMenu,
    reorderMenus,
    deleteMenu,
    updateOpenAppGrant
  }
}
